import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './checkbox_dropdown.module.scss';
import { buildClassName } from 'utils/build-class-name';

export type CheckboxDropdownItem = {
  key: string;
  label: string;
  checked: boolean;
};

export type CheckboxDropdownProps = {
  id: string;
  buttonLabel: string;
  items: CheckboxDropdownItem[];
  onToggle: (key: string, checked: boolean) => void;
  onToggleAll: (checked: boolean) => void;
  disabled?: boolean;
  menuAriaLabel?: string;
  fullWidth?: boolean;
};

function useClickOutside<T extends HTMLElement>(onOutside: () => void) {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!ref.current) return;
      if (e.target instanceof Node && !ref.current.contains(e.target)) {
        onOutside();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onOutside]);
  return ref;
}

// TODO: replace with MUI Checkbox
export default function CheckboxDropdown({
  id = '',
  buttonLabel,
  items,
  onToggle,
  onToggleAll,
  disabled = false,
  menuAriaLabel,
  fullWidth = false,
}: CheckboxDropdownProps) {
  const [isOpen, setOpen] = useState(false);
  const containerRef = useClickOutside<HTMLDivElement>(() => setOpen(false));

  const allSelected = items.length > 0 && items.every(i => i.checked);

  const handleToggle = (key: string, current: boolean) => {
    onToggle?.(key, !current);
  };

  const handleToggleAll = (checked: boolean) => {
    onToggleAll?.(checked);
  };

  const maxWidth = useMemo(() => {
    return `${items.reduce((max, item) => Math.max(max, item.label.length), 0) + 2}ch`;
  }, [items]);

  const buttonId = `dropdown-trigger-${id}`;
  const menuId = `dropdown-menu-${id}`;

  return (
    <div className={styles.dropdown} ref={containerRef}>
      <button
        type="button"
        id={buttonId}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={menuId}
        aria-label={buttonLabel}
        className={buildClassName(styles.dropdownButton, isOpen && styles.open, fullWidth && styles.fullWidth)}
        onClick={() => setOpen(o => !o)}
        disabled={disabled}
        style={{ minWidth: `${buttonLabel.length + 2}ch`, maxWidth: `${maxWidth}ch` }}
      >
        {buttonLabel}
        <span className={styles.caret}>▾</span>
      </button>
      {isOpen && (
        <div id={menuId} className={buildClassName(styles.menu, fullWidth && styles.fullWidth)} role="menu" aria-label={menuAriaLabel} aria-labelledby={buttonId}>
          <button
            type="button"
            className={styles.menuItem}
            onClick={() => handleToggleAll(!allSelected)}
          >
            <span className={styles.menuLabel}>Select All</span>
            <label className={styles.switch} onClick={e => e.stopPropagation()}>
              <input aria-label="Select All" type="checkbox" checked={allSelected} onChange={e => handleToggleAll(e.target.checked)} />
              <span className={styles.slider} />
            </label>
          </button>
          {items.map(item => (
            <button
              key={item.key}
              type="button"
              className={styles.menuItem}
              role="menuitemcheckbox"
              aria-checked={item.checked}
              onClick={() => handleToggle(item.key, item.checked)}
            >
              <span className={styles.menuLabel}>{item.label}</span>
              <label className={styles.switch} onClick={e => e.stopPropagation()}>
                <input type="checkbox" checked={item.checked} onChange={() => handleToggle(item.key, item.checked)} />
                <span className={styles.slider} />
              </label>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


